import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { formatIsoDate } from '../utils/formatDateValue';
import API from '../services/api';
import { SparklesIcon, ArchiveBoxIcon, LinkIcon, ArrowRightIcon, ArrowUturnLeftIcon, ExclamationTriangleIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { enumLabel } from '../utils/enumLabel';

type SubTab = 'review' | 'pending' | 'archived';

interface PendingMatch {
  id: string;
  similarityScore: number;
  llmConfidence: number;
  llmReasoning?: string;
  source?: 'PIPELINE' | 'GATE' | 'MANUAL';
  createdAt: string;
  case: { id: string; caseNumber: string; description?: string; country?: string; region?: string; violationTypes?: string[]; caseStartDate?: string; status: string; createdAt: string };
  matchedCase: { id: string; caseNumber: string; description?: string; country?: string; region?: string; violationTypes?: string[]; caseStartDate?: string; status: string; hasPendingUpdates: boolean } | null;
}

interface PendingCase {
  id: string;
  caseNumber: string;
  description?: string;
  country?: string;
  indigenousGroup?: string;
  violationTypes?: string[];
  status: string;
  updatedAt: string;
}

interface ArchivedRow {
  id: string;
  similarityScore: number;
  llmConfidence: number;
  llmReasoning?: string;
  autoConfirmed: boolean;
  createdAt: string;
  reviewedAt?: string | null;
  reviewedBy?: { id: string; firstName: string; lastName: string } | null;
  substantiveUpdateCount: number;
  case: {
    id: string;
    caseNumber: string;
    description?: string;
    country?: string;
    dataSource?: string;
    sourceInformation?: string;
    status: string;
    createdAt: string;
  };
  matchedCase: { id: string; caseNumber: string; hasPendingUpdates: boolean } | null;
}

const truncate = (s?: string, n = 140) => (s && s.length > n ? `${s.slice(0, n)}…` : s || '');

export default function DuplicatesTab({ initialTab, onTabChange }: { initialTab?: string | null; onTabChange?: (tab: string | null) => void } = {}) {
  const [sub, setSubState] = useState<SubTab>(() => (['review', 'pending', 'archived'].includes(initialTab || '') ? (initialTab as SubTab) : 'review'));
  const setSub = (t: SubTab) => { setSubState(t); onTabChange?.(t === 'review' ? null : t); };
  // Follow the parent's tab when it changes underneath us (in-page link or back button).
  useEffect(() => {
    const t: SubTab = ['review', 'pending', 'archived'].includes(initialTab || '') ? (initialTab as SubTab) : 'review';
    setSubState((cur) => (cur === t ? cur : t));
  }, [initialTab]);

  const review = useQuery<PendingMatch[]>({
    queryKey: ['dedup-pending-matches'],
    queryFn: async () => (await API.get('/api/dedup/pending-matches')).data,
  });

  const pending = useQuery<PendingCase[]>({
    queryKey: ['dedup-pending-updates'],
    queryFn: async () => (await API.get('/api/dedup/pending-updates')).data,
    enabled: sub === 'pending',
  });

  const archived = useQuery<ArchivedRow[]>({
    queryKey: ['dedup-archived'],
    queryFn: async () => (await API.get('/api/dedup/archived-duplicates')).data,
    enabled: sub === 'archived',
  });

  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  // Un-merge: POST /matches/:id/unmerge restores the archived duplicate with the statuses it had
  // before the merge and withdraws what it proposed onto the canonical. (The old Restore PATCHed the
  // match to DISMISSED, which reset the case to FOR_VALIDATION and could queue a paid AI check.)
  const [restoring, setRestoring] = useState<string | null>(null);
  const restore = useMutation({
    mutationFn: async (matchId: string) => (await API.post(`/api/dedup/matches/${matchId}/unmerge`)).data,
    onSuccess: (d: any) => {
      queryClient.invalidateQueries({ queryKey: ['dedup-archived'] });
      queryClient.invalidateQueries({ queryKey: ['dedup-pending-updates'] });
      const withdrawn = d?.withdrawnUpdates
        ? `; ${d.withdrawnUpdates} pending update${d.withdrawnUpdates === 1 ? '' : 's'} withdrawn from ${d.canonicalCaseNumber ?? 'the original'}`
        : '';
      showToast('success', `Un-merged — ${d?.caseNumber ?? 'case'} restored${d?.restoredStatus ? ` as ${enumLabel(d.restoredStatus)}` : ''}${withdrawn}`);
    },
    onError: (err: any) => showToast('error', err?.response?.data?.error || 'Failed to un-merge'),
    onSettled: () => setRestoring(null),
  });
  const handleRestore = async (row: ArchivedRow) => {
    const ok = await confirm({
      title: 'Restore as its own case?',
      message: `Restore ${row.case.caseNumber} as its own case, with the status it had before the merge? Everything the merge put on ${row.matchedCase?.caseNumber ?? 'the original'} is taken back — copied rows, sources, documents, comments, and the fields chosen — except a field someone edited again since the merge.`,
      confirmLabel: 'Un-merge',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;
    setRestoring(row.id);
    restore.mutate(row.id);
  };

  // Deciding happens on /merge/:matchId (the compare page), never here; the page comes back to this
  // tab afterwards via location.state.returnTo.
  const RETURN_TO = '/scraper?section=duplicates&tab=review';

  const tabs: { key: SubTab; label: string; count?: number }[] = [
    { key: 'review', label: 'Needs Review', count: review.data?.length },
    { key: 'pending', label: 'Cases with Proposed Updates', count: pending.data?.length },
    { key: 'archived', label: 'Confirmed Duplicate Cases', count: archived.data?.length },
  ];

  return (
    <div>
      {/* Sub-tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex gap-6">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setSub(t.key)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2 ${
                sub === t.key ? 'border-primary text-primary' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t.label}
              {typeof t.count === 'number' && t.count > 0 && (
                <span className="inline-flex items-center justify-center px-1.5 min-w-[1.25rem] h-5 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">{t.count}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {sub === 'review' && (
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Needs Review</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Possible duplicates the AI wasn't confident enough to auto-decide. Open a pair to see both cases side by side; the decision — confirm the duplicate, or dismiss — is made there.</p>
            </div>
          </div>
          {review.isLoading ? (
            <div className="px-6 py-12 text-center text-gray-400 dark:text-gray-500">Loading…</div>
          ) : !review.data || review.data.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">Nothing awaiting review.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Incoming case</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Possible duplicate of</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Confidence</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Decision</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {review.data.map((m) => (
                  <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-4 py-3 align-top">
                      <Link to={`/cases/${m.case.id}`} className="text-sm font-medium text-primary hover:underline">{m.case.caseNumber}</Link>
                      <div className="text-xs text-gray-500 dark:text-gray-400 max-w-sm">{truncate(m.case.description)}</div>
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{[m.case.country, m.case.region].filter(Boolean).join(' · ') || '—'}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {m.matchedCase ? (
                        <span className="inline-flex items-center gap-1 text-sm">
                          <ArrowRightIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                          <Link to={`/cases/${m.matchedCase.id}`} className="font-medium text-gray-700 dark:text-gray-300 hover:underline">{m.matchedCase.caseNumber}</Link>
                        </span>
                      ) : <span className="text-gray-400 dark:text-gray-500 text-sm">—</span>}
                      {m.matchedCase?.description && <div className="text-xs text-gray-500 dark:text-gray-400 max-w-sm mt-0.5">{truncate(m.matchedCase.description)}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 align-top">
                      <div>{m.llmConfidence}% <span className="text-xs text-gray-400 dark:text-gray-500">({Math.round(m.similarityScore * 100)}% sim)</span></div>
                      {m.llmReasoning && <div className="text-xs text-gray-400 dark:text-gray-500 max-w-xs mt-0.5" title={m.llmReasoning}>{truncate(m.llmReasoning, 140)}</div>}
                    </td>
                    <td className="px-4 py-3 text-right align-top">
                      <Link
                        to={`/merge/${m.id}`}
                        state={{ returnTo: RETURN_TO }}
                        title="See both cases side by side and decide there"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <ArrowsRightLeftIcon className="h-3.5 w-3.5" /> Compare and decide
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {sub === 'pending' && (
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-violet-500" />
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Cases with Proposed Updates</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Cases a newer report proposes updates for. Open a case to review each suggested change against its source, then apply the correct ones or dismiss the rest.</p>
            </div>
          </div>
          {pending.isLoading ? (
            <div className="px-6 py-12 text-center text-gray-400 dark:text-gray-500">Loading…</div>
          ) : !pending.data || pending.data.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">No cases are awaiting update review.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Case</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Country</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {pending.data.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-4 py-3">
                      <Link to={`/cases/${c.id}`} className="text-sm font-medium text-primary hover:underline">{c.caseNumber}</Link>
                      <div className="text-xs text-gray-500 dark:text-gray-400 max-w-md">{truncate(c.description)}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{c.country || '—'}</td>
                    <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{c.status}</span></td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400"><span title={formatIsoDate(c.updatedAt) || c.updatedAt}>{formatDistanceToNow(new Date(c.updatedAt), { addSuffix: true })}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {sub === 'archived' && (
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
            <ArchiveBoxIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Confirmed Duplicate Cases</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Cases confirmed as duplicates and archived, merged into the case they duplicate. Check the confidence and reasoning; Un-merge any that were archived in error.</p>
            </div>
          </div>
          {archived.isLoading ? (
            <div className="px-6 py-12 text-center text-gray-400 dark:text-gray-500">Loading…</div>
          ) : !archived.data || archived.data.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">No cases have been archived as duplicates yet.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Archived duplicate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Merged into</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Confidence</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Contributed</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Confirmed</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {archived.data.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-4 py-3">
                      <Link to={`/cases/${row.case.id}`} className="text-sm font-medium text-primary hover:underline">{row.case.caseNumber}</Link>
                      <div className="text-xs text-gray-500 dark:text-gray-400 max-w-sm">{truncate(row.case.description)}</div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                        {row.case.dataSource && <span>{row.case.dataSource}</span>}
                        {row.case.sourceInformation && (
                          <a href={row.case.sourceInformation} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                            <LinkIcon className="h-3 w-3" /> source
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {row.matchedCase ? (
                        <span className="inline-flex items-center gap-1 text-sm">
                          <ArrowRightIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                          <Link to={`/cases/${row.matchedCase.id}`} className="font-medium text-gray-700 dark:text-gray-300 hover:underline">{row.matchedCase.caseNumber}</Link>
                          {row.matchedCase.hasPendingUpdates && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[11px] rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300"><SparklesIcon className="h-3 w-3" />pending</span>
                          )}
                        </span>
                      ) : <span className="text-gray-400 dark:text-gray-500 text-sm">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 align-top">
                      <div>{row.llmConfidence}%{row.autoConfirmed && <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">(auto)</span>}</div>
                      {row.llmReasoning && <div className="text-xs text-gray-400 dark:text-gray-500 max-w-xs mt-0.5" title={row.llmReasoning}>{truncate(row.llmReasoning, 120)}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {row.substantiveUpdateCount > 0 ? (
                        <span className="inline-flex items-center gap-1 text-violet-700 dark:text-violet-300"><SparklesIcon className="h-3.5 w-3.5" />{row.substantiveUpdateCount} update{row.substantiveUpdateCount === 1 ? '' : 's'}</span>
                      ) : <span className="text-gray-400 dark:text-gray-500">source only</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 align-top">
                      <div title={formatIsoDate(row.reviewedAt || row.createdAt) || undefined}>{formatDistanceToNow(new Date(row.reviewedAt || row.createdAt), { addSuffix: true })}</div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">
                        {row.reviewedBy ? `by ${row.reviewedBy.firstName} ${row.reviewedBy.lastName}` : (row.autoConfirmed ? 'auto-confirmed' : '')}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right align-top">
                      <button
                        disabled={restoring === row.id}
                        onClick={() => handleRestore(row)}
                        title="Restore as its own case and withdraw its pending updates"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        <ArrowUturnLeftIcon className="h-3.5 w-3.5" /> {restoring === row.id ? 'Un-merging…' : 'Un-merge'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

    </div>
  );
}
